import { getHomePageModules, getHomepageOpenModules, HomepageOpenModulesResponse } from '@/api/newApi/homePage';
import {
  HOMEPAGE_OPEN_MODULE_HOT_SEARCH,
  HOMEPAGE_OPEN_MODULE_RECOMMEND_TEXT_LINK,
} from '@/constants/homepageOpenModules';
import { getHomePageImageUrl } from '@/util/getImg';

const LCP_PRELOAD_LINK_ID = 'homepage-lcp-banner-preload';

type HomePageApiResult = Awaited<ReturnType<typeof getHomePageModules>>;

let cachedResult: HomePageApiResult | null = null;
let inflightRequest: Promise<HomePageApiResult> | null = null;
let cachedOpenModules: HomepageOpenModulesResponse | null = null;
let openModulesInflight: Promise<HomepageOpenModulesResponse> | null = null;

/** 读取内存中已预取的首页配置，无缓存时返回 null */
export function getCachedHomePageConfig(): HomePageApiResult | null {
  return cachedResult;
}

export function clearHomePageConfigCache() {
  cachedResult = null;
  inflightRequest = null;
  cachedOpenModules = null;
  openModulesInflight = null;
}

/** 读取内存中已预取的 NavBar Open 模块配置 */
export function getCachedHomepageOpenModules(): HomepageOpenModulesResponse | null {
  return cachedOpenModules;
}

/** NavBar 热搜词 / 推荐文字链：按需加载 Open 模块，不拉全量 homepage */
export function fetchHomepageOpenModules(forceRefresh = false): Promise<HomepageOpenModulesResponse> {
  if (!forceRefresh && cachedOpenModules) {
    return Promise.resolve(cachedOpenModules);
  }

  if (!forceRefresh && openModulesInflight) {
    return openModulesInflight;
  }

  openModulesInflight = getHomepageOpenModules()
    .then(result => {
      cachedOpenModules = result;
      return result;
    })
    .finally(() => {
      openModulesInflight = null;
    });

  return openModulesInflight;
}

const pickModuleList = (value: Record<string, any> = {}, field: 'hotSearchWords' | 'recommendTextLinks') => {
  if (Array.isArray(value[field])) {
    return value[field];
  }

  const hotSearchModule = value[HOMEPAGE_OPEN_MODULE_HOT_SEARCH];
  const recommendModule = value[HOMEPAGE_OPEN_MODULE_RECOMMEND_TEXT_LINK];
  const modules = value.modules;

  if (field === 'hotSearchWords') {
    if (Array.isArray(hotSearchModule?.hotSearchWords)) {
      return hotSearchModule.hotSearchWords;
    }
    if (Array.isArray(hotSearchModule)) {
      return hotSearchModule;
    }
    if (Array.isArray(modules)) {
      const matched = modules.find(
        (item: any) =>
          item?.moduleCode === HOMEPAGE_OPEN_MODULE_HOT_SEARCH ||
          item?.moduleType === HOMEPAGE_OPEN_MODULE_HOT_SEARCH,
      );
      return matched?.hotSearchWords || matched?.config?.hotSearchWords || [];
    }
    return [];
  }

  if (Array.isArray(recommendModule?.recommendTextLinks)) {
    return recommendModule.recommendTextLinks;
  }
  if (Array.isArray(recommendModule)) {
    return recommendModule;
  }
  if (Array.isArray(modules)) {
    const matched = modules.find(
      (item: any) =>
        item?.moduleCode === HOMEPAGE_OPEN_MODULE_RECOMMEND_TEXT_LINK ||
        item?.moduleType === HOMEPAGE_OPEN_MODULE_RECOMMEND_TEXT_LINK,
    );
    return matched?.recommendTextLinks || matched?.config?.recommendTextLinks || [];
  }
  return [];
};

/** 将 openModules 响应归一为 NavBar 解析所需结构 */
export function normalizeHomepageOpenModulesValue(value: Record<string, any> | null | undefined = {}) {
  const payload = value || {};
  const nestedData = payload.data && typeof payload.data === 'object' ? payload.data : {};

  return {
    hotSearchWords: pickModuleList({ ...nestedData, ...payload }, 'hotSearchWords'),
    recommendTextLinks: pickModuleList({ ...nestedData, ...payload }, 'recommendTextLinks'),
  };
}

/** 首页 router 预取 + HomePage 复用：同一时刻只发起一次 /jdig/settings/v2/homepage 请求 */
export function fetchHomePageConfig(forceRefresh = false): Promise<HomePageApiResult> {
  if (!forceRefresh && cachedResult) {
    return Promise.resolve(cachedResult);
  }

  if (!forceRefresh && inflightRequest) {
    return inflightRequest;
  }

  inflightRequest = getHomePageModules()
    .then(result => {
      cachedResult = result;
      return result;
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
}

export function normalizeHomePageConfig(result: HomePageApiResult | null | undefined) {
  return {
    ...(result?.value || {}),
    ...(result?.value?.data || {}),
  };
}

export function getHomePageModuleList(result: HomePageApiResult | null | undefined) {
  const homePageConfig = normalizeHomePageConfig(result);
  return homePageConfig?.modules || result?.value?.modules || result?.value?.data?.modules || [];
}

export function getFirstBigBannerImageUrl(modules: any[] = []): string {
  const bigBannerModule = modules.find(item => item?.moduleType === '10100-01');
  const banners = bigBannerModule?.bigBannerConfig?.banners;
  if (!banners?.length) {
    return '';
  }

  const firstBanner = [...banners].sort((prev, next) => (prev?.displayOrder || 0) - (next?.displayOrder || 0))[0];
  return firstBanner?.image ? getHomePageImageUrl(firstBanner.image) : '';
}

export function preloadHomePageLcpImage(imageUrl?: string): void {
  if (!imageUrl || typeof document === 'undefined') {
    return;
  }

  const existing = document.getElementById(LCP_PRELOAD_LINK_ID) as HTMLLinkElement | null;
  if (existing) {
    if (existing.href !== imageUrl) {
      existing.href = imageUrl;
    }
    return;
  }

  const link = document.createElement('link');
  link.id = LCP_PRELOAD_LINK_ID;
  link.rel = 'preload';
  link.as = 'image';
  link.href = imageUrl;
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}

export function preloadHomePageLcpFromConfig(result: HomePageApiResult | null | undefined): void {
  const imageUrl = getFirstBigBannerImageUrl(getHomePageModuleList(result));
  preloadHomePageLcpImage(imageUrl);
}

export function parseRecommendTextLinks(homePageConfig: Record<string, any>) {
  return (homePageConfig?.recommendTextLinks || [])
    .filter((item: any) => item?.text)
    .sort((prev: any, next: any) => (prev?.sort || 0) - (next?.sort || 0))
    .map((item: any) => ({
      name: item.text,
      jumpUrl: item.jumpUrl || '',
      openType: 4,
      jumpType: item.jumpType || 'url',
      keyword: item.keyword || '',
      categoryId: item.categoryId || '',
    }));
}

export function parseHotSearchWords(homePageConfig: Record<string, any>) {
  return (homePageConfig?.hotSearchWords || [])
    .filter((item: any) => item?.text)
    .sort((prev: any, next: any) => (prev?.sort || 0) - (next?.sort || 0))
    .map((item: any, index: number) => ({
      id: item.id || `${item.text}-${index}`,
      sort: item.sort || index + 1,
      text: item.text,
    }));
}
